import type { MediaDto } from '@/lib/sanity/dto';
import { isSvgImage, resolveImageAlt, sanityImage } from '@/lib/sanity/image';
import type { LightboxFilmSlide, LightboxImageSlide } from './lightbox.types';

const FALLBACK_DIM = { width: 1200, height: 900 };

// The film dialog's box (media-lightbox.css): 16:9, drawn 1100px wide at most.
// A file asset carries no dimensions, so a film is given the box rather than
// its own size — the player letterboxes whatever does not fit it.
const FILM_BOX = { width: 1100, height: 619 };

export function buildLightboxSlide(image: MediaDto): LightboxImageSlide {
    const asset = image.asset;
    const dim = asset?.metadata?.dimensions;
    const { width, height } = dim && dim.width > 0 && dim.height > 0 ? dim : FALLBACK_DIM;

    return {
        src: sanityImage.gallery(image) ?? asset?.url ?? '',
        width,
        height,
        alt: resolveImageAlt(image),
        // SVGs need a custom renderer to scale up (see LightboxRoot).
        isSvg: isSvgImage(image),
    };
}

/** Slides for one section's pictures, in render order — one group to page through. */
export function buildLightboxSlides(images: MediaDto[]): LightboxImageSlide[] {
    return images.map(buildLightboxSlide);
}

/** A film as a slide: it plays where it lands, its still as the poster until then. */
export function buildLightboxFilmSlide(video: MediaDto, poster?: MediaDto): LightboxFilmSlide {
    return {
        type: 'video',
        // An empty type is no refusal — the browser then tries the file itself.
        sources: [{ src: video.asset?.url ?? '', type: video.asset?.mimeType ?? '' }],
        poster: sanityImage.heroPoster(poster),
        ...FILM_BOX,
    };
}
